import { MatchQuality } from './match-quantity.model';

/**
 * HOW A MATCH BAND COUNTS TOWARD A RANKING. ONE RULE, FOR EVERY LIST.
 *
 * `api.MatchQuality` runs NA 0, Poor 1, Fair 2, Good 3, Great 4, Excellent 5,
 * Overqualified 6. The 6 is an ordinary primary key, not a sixth grade: the
 * matching procedure sets it from EDUCATION, by overwriting SkillsMatch with
 * 101 when the learner's level sits two or more above the occupation's and the
 * education match is over 75. Adding the four ids up therefore scored a role
 * the learner is plainly past HIGHER than a perfect one.
 *
 * This lived only inside the career-list sort, which is not the list the
 * member sees first. `/career-planning/build-your-target` renders the home
 * page, and the home page's "Best fit for you" does not sort at all: it takes
 * the order the cards arrive in, and that order is the raw sum computed in
 * `occupation-cards.service`. So the fix applied to one list and the reported
 * surface kept showing 6, 6, 6, 5, 5, 5 down the page.
 *
 * Measured on dev, user 70956: Veterinary Technologists and Technicians, Sales
 * Representatives and General and Operations Managers all carried
 * `skillsMatchQualityId` 6 with `skillsmatch` 101, and all three sat above
 * Anesthesiologist Assistants, Logisticians and Software Developers, which
 * carried a genuine 5.
 *
 * Kept here rather than in either list so there is one definition to change.
 */

/** A shape that carries the four band ids. Both card types satisfy it. */
export interface MatchBands {
  skillsMatchQualityId?: number | null;
  interestsMatchQualityId?: number | null;
  valuesMatchQualityId?: number | null;
  lifestyleMatchQualityId?: number | null;
}

/**
 * What one band contributes. Overqualified contributes NOTHING rather than the
 * most, because it is a warning about the role and not a grade for it.
 */
export function bandScore(quality: number | null | undefined): number {
  return quality === MatchQuality.Overqualified ? 0 : (quality ?? 0);
}

/** How many of the four dimensions came back overqualified. */
export function overqualifiedCount(card: MatchBands): number {
  return [
    card.skillsMatchQualityId,
    card.interestsMatchQualityId,
    card.valuesMatchQualityId,
    card.lifestyleMatchQualityId
  ].filter((q) => q === MatchQuality.Overqualified).length;
}

/** The four bands added up, with the warning worth nothing. */
export function alignmentRank(card: MatchBands): number {
  return (
    bandScore(card.skillsMatchQualityId) +
    bandScore(card.interestsMatchQualityId) +
    bandScore(card.valuesMatchQualityId) +
    bandScore(card.lifestyleMatchQualityId)
  );
}

/* -------------------------------------------------------------------------
   HOW FAR BELOW THE LEARNER A ROLE SITS.

   This lived in the quick-match filter, which is the list at
   /career-planning/quick-match. The list a member lands on is the home page,
   served at /career-planning/build-your-target, and it takes the order the
   cards arrive in. So the education term applied on one screen and not the
   other, and the screen it missed is the reported one: a High School Diploma
   role could still sit at the top for somebody holding a degree, on interests,
   values and lifestyle alone.

   Moved here so one definition serves both. Re-exported from the filter, so
   every existing import and test keeps working.
   ------------------------------------------------------------------------- */

/** Least schooling first. */
export const EDUCATION_ORDER: string[] = [
  'Less than a High School Diploma',
  'High School Diploma',
  'Post-Secondary Certificate',
  'Some College Courses',
  "Associate's Degree",
  "Bachelor's Degree",
  'Post-Baccalaureate Certificate',
  "Master's Degree",
  "Post-Master's Certificate",
  'First Professional Degree',
  'Doctoral Degree',
  'Post-Doctoral Training'
];

/**
 * Levels that sit AT an existing rung rather than between two of them.
 *
 * A CCAF degree is the Air Force's associate degree; it is not a step between
 * an Associate's and a Bachelor's. Giving it its own rung stretched every
 * distance past it by one, which matters because the penalty is measured in
 * rungs: an airman holding a CCAF degree would have had associate-level work
 * counted as further beneath them than it is.
 */
export const EDUCATION_ALIASES: Record<string, string> = {
  'ccaf degree': "Associate's Degree",
  'some ccaf courses': 'Some College Courses',
  "some master's courses": 'Post-Baccalaureate Certificate'
};

/**
 * Where a level description sits on the ladder, or -1.
 *
 * Case-insensitive and trimmed, because the two sides of this comparison come
 * from different tables and a ladder keyed on exact strings is one rename away
 * from being silently dead. It WAS silently dead once already.
 */
export function educationLadderIndex(
  description: string | null | undefined
): number {
  const raw = (description ?? '').trim().toLowerCase();

  if (!raw) {
    return -1;
  }

  const term = (EDUCATION_ALIASES[raw] ?? raw).toLowerCase();

  return EDUCATION_ORDER.findIndex((level) => level.toLowerCase() === term);
}

/** Anything carrying the education level a role typically asks for. */
export interface HasTypicalEducation {
  typicalEducationLevelGroup?: string | null;
}

/**
 * HOW FAR BELOW THE LEARNER THIS OCCUPATION SITS, in ladder rungs.
 *
 * ONE STEP DOWN IS NOT A PENALTY. A learner with a Bachelor's looking at work
 * that typically asks for an Associate's is a normal, often deliberate choice,
 * and demoting it would be the app telling them their options are beneath
 * them. Two or more rungs is where the list stops being useful.
 *
 * `api.usermatches.overqualified` cannot carry this: it is set on 2,982 of
 * 4,009,094 rows on dev, 0.07%, and the card never receives it anyway.
 */
export function levelsBelowLearner(
  card: HasTypicalEducation,
  learnerLevel: number | null
): number {
  if (learnerLevel == null || learnerLevel < 0) {
    return 0;
  }

  const cardLevel = educationLadderIndex(card.typicalEducationLevelGroup);

  if (cardLevel === -1) {
    return 0;
  }

  return Math.max(0, learnerLevel - cardLevel - 1);
}

/**
 * Best fit first.
 *
 * Three keys, in this order, and the order is the point:
 *
 *   1. Overqualified, which sinks ahead of everything else. No arrangement of
 *      interests, values and lifestyle makes a role the learner has already
 *      outgrown the best thing to show them under "Best fit for you".
 *   2. How far below their education the role sits, two rungs or more.
 *   3. The four survey bands added up.
 *
 * Deliberately three keys rather than points subtracted from one score: mixing
 * them lets a big enough survey score float a role two levels below the learner
 * back to the top, which is the complaint this exists to answer.
 *
 * `learnerLevel` is optional. Passing nothing means "no education on file", and
 * then this is exactly the two-key ordering it was before, which is what a
 * caller that cannot know the member should get. Nothing is ever removed from
 * the list; a role below the learner still appears, just not above work that
 * fits.
 */
export function compareByFit(
  a: MatchBands & HasTypicalEducation,
  b: MatchBands & HasTypicalEducation,
  learnerLevel: number | null = null
): number {
  const overqualified = overqualifiedCount(a) - overqualifiedCount(b);

  if (overqualified !== 0) {
    return overqualified;
  }

  const fit =
    levelsBelowLearner(a, learnerLevel) - levelsBelowLearner(b, learnerLevel);

  return fit !== 0 ? fit : alignmentRank(b) - alignmentRank(a);
}

/**
 * The learner's HIGHEST COMPLETED education, as a ladder index, or null.
 *
 * Completed only. A qualification somebody is part-way through is a goal, not
 * something they hold, and counting it would demote the very work they are
 * studying toward. `completed` is undefined on rows written before the survey
 * asked for it, and those read as held, which is what every other surface
 * already assumes of them.
 */
export function learnerEducationLadderIndex(
  educations:
    | { educationLevelShortDescr?: string | null; completed?: boolean | null }[]
    | null
    | undefined
): number | null {
  const levels = (educations ?? [])
    .filter((e) => e?.completed !== false)
    .map((e) => educationLadderIndex(e?.educationLevelShortDescr))
    .filter((index) => index >= 0);

  return levels.length ? Math.max(...levels) : null;
}
